import sha1 from 'js-sha1';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { SUPPORTED_METHODS } from '@savgroup-front-common/constants/shared';

import { deleteFileAttachmentRequest } from '../../api/Attachment/deleteFileAttachmentCommand';
import { getDocumentStatusByFileRequest } from '../../api/Attachment/getDocumentStatusByFileQuery';
import { getDocumentsStatusByFileRequest } from '../../api/Attachment/getDocumentsStatusByFileQuery';
import { listFilesAttachmentRequest } from '../../api/Attachment/listFilesAttachmentQuery';
import { loadDocumentByUrlRequest } from '../../api/Attachment/loadDocumentByUrlQuery';
import { setFileAttachmentInfoRequest } from '../../api/Attachment/setFileAttachmentInfoCommand';
import { getClaimDocumentsRequest } from '../../api/Claim/getClaimDocuments';
import { arrayBufferFromBlob, getFileType } from '../../helpers';
import { executeSagaRequest, reduxExtendedRequestSaga } from '../../services';
import { selectLocale } from '../i18n/selectors';

import { getDocumentsStatusByFileId } from './actionCreators';
import {
  DELETE_ATTACHMENT_FROM_FILE,
  GET_ALL_DOCUMENTS_STATUS_BY_FILE_ID,
  GET_DOCUMENTS_BY_CLAIM_ID,
  GET_DOCUMENTS_STATUS_BY_FILE_ID,
  LOAD_DOCUMENT_BY_URL,
  LOAD_FILES_ATTACHMENT_LIST,
  REFRESH_DOCUMENTS_STATUS_BY_FILE_ID,
  SET_DOCUMENT_BY_URL,
  SET_FILE_ATTACHMENT_INFO,
  UPLOAD_FILE_ATTACHMENT_DATA,
} from './actionTypes';
import { attachmentsOrigins } from './constants';
import {
  getDocumentsStatusByFileIdValue,
  selectLoadDocumentByUrlWasLoaded,
} from './selectors';

const { EXTERNAL_CARRIER_LABEL_ATTACHMENT } = attachmentsOrigins;

export function* loadDocumentByUrlWorker({ payload: { url, fileName } }) {
  if (!fileName || !url) {
    return;
  }

  const wasLoaded = yield select((state) =>
    selectLoadDocumentByUrlWasLoaded(state, { indexer: url }),
  );

  if (wasLoaded) {
    yield put(LOAD_DOCUMENT_BY_URL.end());

    return;
  }

  yield executeSagaRequest(
    LOAD_DOCUMENT_BY_URL,
    loadDocumentByUrlRequest({ url }),
    {
      indexer: url,
      attachmentId: url,
      filename: fileName,
    },
  );
  yield put(LOAD_DOCUMENT_BY_URL.end({ indexer: url }));
}
function* loadDocumentByUrlWatcher() {
  yield takeEvery(LOAD_DOCUMENT_BY_URL.BASE, loadDocumentByUrlWorker);
}

function* setDocumentByUrlWorker({ payload }) {
  const { file, attachmentId, url, filename } = payload;

  yield put(
    LOAD_DOCUMENT_BY_URL.success(file, {
      indexer: url,
      url,
      attachmentId,
      filename,
    }),
  );

  yield put(SET_DOCUMENT_BY_URL.end({ indexer: url }));
}

function* setDocumentByUrlWatcher() {
  yield takeEvery(SET_DOCUMENT_BY_URL.BASE, setDocumentByUrlWorker);
}

export function* loadFilesAttachmentListWorker({ payload = {} }) {
  const { fileId, type } = payload;

  yield executeSagaRequest(
    LOAD_FILES_ATTACHMENT_LIST,
    listFilesAttachmentRequest({ fileId, type }),
    { fileId, type },
  );

  yield put(LOAD_FILES_ATTACHMENT_LIST.end(fileId));
}
export function* loadFilesAttachmentListWatcher() {
  yield takeEvery(
    LOAD_FILES_ATTACHMENT_LIST.BASE,
    loadFilesAttachmentListWorker,
  );
}

function getUploadAttachmentApiUrl({ type, fileId, service }) {
  if (type === EXTERNAL_CARRIER_LABEL_ATTACHMENT) {
    return `${APIConfiguration.carrier}externalCarrierDepositLabels/data`;
  }

  return `${APIConfiguration.document}files/${fileId}/${service}-attachments`;
}
function* uploadFileAttachmentDataWorker({ payload }) {
  const { type, file, fileId, service, fileButtonOrigin } = payload;
  const language = yield select(selectLocale);
  const fileArrayBuffer = yield call(arrayBufferFromBlob, file);
  const internalId = sha1(fileArrayBuffer);
  const uint8 = new Uint8Array(fileArrayBuffer);
  const fileType = getFileType(uint8);

  const fileToUpload = fileType
    ? new File(
        [fileArrayBuffer],
        fileType.ext
          ? file.name.replace(/(\.[^/.]*)?$/, `.${fileType.ext}`)
          : file.name,
        {
          type: fileType.mime || file.type,
        },
      )
    : file;

  const mimeType = fileToUpload.type;

  const body = new FormData();

  body.append('file', fileToUpload);
  const meta = {
    type,
    id: internalId,
    extension: mimeType.substring(mimeType.indexOf('/') + 1),
    fileName: fileToUpload.name,
    mimeType: fileToUpload.type,
    fileId,
    file,
    fileButtonOrigin,
  };

  yield call(
    reduxExtendedRequestSaga,
    UPLOAD_FILE_ATTACHMENT_DATA,
    getUploadAttachmentApiUrl({ type, fileId, service }),
    {
      method: SUPPORTED_METHODS.POST,
      body,
      language,
    },
    meta,
  );

  yield put(UPLOAD_FILE_ATTACHMENT_DATA.end(meta));
}
function* uploadFileAttachmentDataWatcher() {
  yield takeEvery(
    UPLOAD_FILE_ATTACHMENT_DATA.BASE,
    uploadFileAttachmentDataWorker,
  );
}

function* deleteFileAttachmentDataWorker({ payload }) {
  const { fileId, fileAttachmentId, internalId, service, type = null } =
    payload;

  if (!fileAttachmentId) {
    yield put(DELETE_ATTACHMENT_FROM_FILE.end());

    return;
  }

  yield executeSagaRequest(
    DELETE_ATTACHMENT_FROM_FILE,
    deleteFileAttachmentRequest({ fileId, fileAttachmentId, service, type }),
    { fileId, internalId },
  );

  yield put(DELETE_ATTACHMENT_FROM_FILE.end());
}
function* deleteFileAttachmentDataWatcher() {
  yield takeEvery(
    DELETE_ATTACHMENT_FROM_FILE.BASE,
    deleteFileAttachmentDataWorker,
  );
}

function* setFileAttachmentInfoWorker({
  payload: {
    fileId,
    extension,
    name,
    fileAttachmentId,
    service,
    transitionInfo,
    type,
  },
}) {
  yield executeSagaRequest(
    SET_FILE_ATTACHMENT_INFO,
    setFileAttachmentInfoRequest({
      fileId,
      extension,
      name,
      fileAttachmentId,
      service,
      transitionInfo,
      type,
    }),
    { fileId, fileAttachmentId },
  );

  yield put(SET_FILE_ATTACHMENT_INFO.end());
}
function* setFileAttachmentInfoWatcher() {
  yield takeEvery(SET_FILE_ATTACHMENT_INFO.BASE, setFileAttachmentInfoWorker);
}

function* getDocumentsStatusByFileIdWorker({ payload: { fileId, type } }) {
  yield executeSagaRequest(
    GET_DOCUMENTS_STATUS_BY_FILE_ID,
    getDocumentStatusByFileRequest({ fileId, type }),
    {
      fileId,
      type,
      indexer: fileId,
    },
  );
  yield put(GET_DOCUMENTS_STATUS_BY_FILE_ID.end());
}
function* getDocumentsStatusByFileIdWatcher() {
  yield takeEvery(
    GET_DOCUMENTS_STATUS_BY_FILE_ID.BASE,
    getDocumentsStatusByFileIdWorker,
  );
}

function* getAllDocumentsStatusByFileIdWorker({ payload: { fileId, type } }) {
  yield executeSagaRequest(
    GET_ALL_DOCUMENTS_STATUS_BY_FILE_ID,
    getDocumentsStatusByFileRequest({ fileId }),
    {
      fileId,
      type,
      indexer: fileId,
    },
  );
  yield put(GET_ALL_DOCUMENTS_STATUS_BY_FILE_ID.end());
}
function* getAllDocumentsStatusByFileIdWatcher() {
  yield takeEvery(
    GET_ALL_DOCUMENTS_STATUS_BY_FILE_ID.BASE,
    getAllDocumentsStatusByFileIdWorker,
  );
}

function* refreshDocumentsStatusByFileIdWorker({ payload: { fileId } }) {
  const documentsStatus = yield select(
    getDocumentsStatusByFileIdValue,
    fileId,
  );

  const types = Object.keys(documentsStatus ?? {}).filter(
    (type) => type !== 'value',
  );

  yield all(
    types.map((type) => put(getDocumentsStatusByFileId({ fileId, type }))),
  );
}
function* refreshDocumentsStatusByFileIdWatcher() {
  yield takeEvery(
    REFRESH_DOCUMENTS_STATUS_BY_FILE_ID.BASE,
    refreshDocumentsStatusByFileIdWorker,
  );
}

function* getDocumentsByClaimIdWorker({ payload }) {
  const { claimId } = payload;
  const meta = { claimId, indexer: claimId };

  yield executeSagaRequest(
    GET_DOCUMENTS_BY_CLAIM_ID,
    getClaimDocumentsRequest({ claimId }),
    meta,
  );

  yield put(GET_DOCUMENTS_BY_CLAIM_ID.end(meta));
}
function* getDocumentsByClaimIdWatcher() {
  yield takeLatest(GET_DOCUMENTS_BY_CLAIM_ID.BASE, getDocumentsByClaimIdWorker);
}

export default function* mainSaga() {
  try {
    yield all([
      loadFilesAttachmentListWatcher(),
      setFileAttachmentInfoWatcher(),
      uploadFileAttachmentDataWatcher(),
      loadDocumentByUrlWatcher(),
      getDocumentsStatusByFileIdWatcher(),
      getAllDocumentsStatusByFileIdWatcher(),
      refreshDocumentsStatusByFileIdWatcher(),
      deleteFileAttachmentDataWatcher(),
      getDocumentsByClaimIdWatcher(),
      setDocumentByUrlWatcher(),
    ]);
  } catch (error) {
    logCritical(error);
  }
}
